import { useEffect, useRef, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Info, CheckCircle, AlertTriangle, Clock, Star, Gift, Skull, Loader2, Palmtree, Camera } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { ChatImageBubble } from "@/components/chat/ChatImageBubble";
import { ImageLightbox } from "@/components/chat/ImageLightbox";

interface Message {
  _key: string;
  from: string;
  to: string;
  encrypted_message: string;
  nonce: string;
  date: string;
  is_system: boolean;
  points?: number;
  message_type?: "text" | "image";
  // System message fields (decrypted by parent)
  type?: string;
  entity_name?: string;
  entity_icon?: string;
  encrypted_note?: string;
  ref_name?: string;
  ref_icon?: string;
  streak_days?: number;
  streak_factor?: number;
}

interface ChatViewProps {
  messages: Message[];
  currentUserId: string;
  onSend: (text: string) => Promise<void>;
  onCaptureImage?: () => void;
  decryptBinary?: (encrypted: string) => Promise<Uint8Array | null>;
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

function getSystemMessageStyle(msg: Message) {
  const t = msg.type || "";

  if (t === "chore_overdue") {
    return {
      icon: <Clock className="w-3.5 h-3.5" />,
      bg: "bg-red-500/10 border-red-500/20",
      text: "text-red-400",
      accent: "text-red-500",
    };
  }
  if (t === "rule_broken") {
    return {
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      bg: "bg-amber-500/10 border-amber-500/20",
      text: "text-amber-400",
      accent: "text-amber-500",
    };
  }
  if (t === "chore_completed") {
    return {
      icon: <CheckCircle className="w-3.5 h-3.5" />,
      bg: "bg-emerald-500/10 border-emerald-500/20",
      text: "text-emerald-400",
      accent: "text-emerald-500",
    };
  }
  if (t === "rule_streak") {
    return {
      icon: <Star className="w-3.5 h-3.5" />,
      bg: "bg-purple-500/10 border-purple-500/20",
      text: "text-purple-400",
      accent: "text-purple-500",
    };
  }
  if (t === "reward_redeemed" || t === "reward_reversed") {
    return {
      icon: <Gift className="w-3.5 h-3.5" />,
      bg: "bg-blue-500/10 border-blue-500/20",
      text: "text-blue-400",
      accent: "text-blue-500",
    };
  }
  if (t === "punishment_redeemed" || t === "punishment_reversed") {
    return {
      icon: <Skull className="w-3.5 h-3.5" />,
      bg: "bg-rose-500/10 border-rose-500/20",
      text: "text-rose-400",
      accent: "text-rose-500",
    };
  }
  if (t === "holiday_activated" || t === "holiday_approved") {
    return {
      icon: <Palmtree className="w-3.5 h-3.5" />,
      bg: "bg-amber-500/10 border-amber-500/20",
      text: "text-amber-400",
      accent: "text-amber-500",
    };
  }
  if (t === "holiday_deactivated" || t === "holiday_expired") {
    return {
      icon: <Palmtree className="w-3.5 h-3.5" />,
      bg: "bg-emerald-500/10 border-emerald-500/20",
      text: "text-emerald-400",
      accent: "text-emerald-500",
    };
  }
  if (t === "holiday_requested" || t === "holiday_cancelled") {
    return {
      icon: <Palmtree className="w-3.5 h-3.5" />,
      bg: "bg-blue-500/10 border-blue-500/20",
      text: "text-blue-400",
      accent: "text-blue-500",
    };
  }
  if (t === "holiday_denied") {
    return {
      icon: <Palmtree className="w-3.5 h-3.5" />,
      bg: "bg-red-500/10 border-red-500/20",
      text: "text-red-400",
      accent: "text-red-500",
    };
  }
  return {
    icon: <Info className="w-3.5 h-3.5" />,
    bg: "bg-app-surface-raised border-app-border/30",
    text: "text-app-text-muted",
    accent: "text-app-primary opacity-70",
  };
}

/** Compose a display string for system messages from structured type data. */
function formatSystemMessage(msg: Message): string {
  const name = msg.entity_name || "[Encrypted]";
  const pts = msg.points ?? 0;
  switch (msg.type) {
    case "chore_completed": return `✅ Completed ${name} (+${pts})`;
    case "chore_overdue": return `⏰ Overdue: ${name} (${pts})`;
    case "rule_broken": return `⚠️ Rule broken: ${name} (${pts})${msg.encrypted_note ? " — " + msg.encrypted_note : ""}`;
    case "rule_streak": return `🔥 ${name} streak: ${msg.streak_days}d (+${pts})`;
    case "punishment_redeemed": return `💀 Punishment: ${msg.ref_name || name} (${pts})`;
    case "reward_redeemed": return `🎁 Reward: ${msg.ref_name || name} (${pts})`;
    case "punishment_reversed": return `↩️ Reversed punishment: ${msg.ref_name || name} (+${Math.abs(pts)})`;
    case "reward_reversed": return `↩️ Reversed reward: ${msg.ref_name || name} (${pts})`;
    case "holiday_activated":
    case "holiday_deactivated":
    case "holiday_requested":
    case "holiday_approved":
    case "holiday_denied":
    case "holiday_cancelled":
    case "holiday_expired":
      return msg.encrypted_message || msg.type?.replace(/_/g, " ") || "Holiday event";
    default: return msg.type?.replace(/_/g, " ") || "Event";
  }
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-app-surface-raised text-app-text rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-app-text-muted animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 rounded-full bg-app-text-muted animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 rounded-full bg-app-text-muted animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

function getDateLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export function ChatView({ messages, currentUserId, onSend, onCaptureImage, decryptBinary, loading, hasMore, loadingMore, onLoadMore }: ChatViewProps) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const prevMessageCountRef = useRef(0);
  const initialScrollDone = useRef(false);

  // Scroll to bottom helper
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  // On initial mount / first messages load, scroll to bottom
  useEffect(() => {
    if (messages.length > 0 && !initialScrollDone.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
        initialScrollDone.current = true;
        prevMessageCountRef.current = messages.length;
        if (scrollRef.current) {
          prevScrollHeightRef.current = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [messages.length, scrollToBottom]);

  // When new messages arrive at the bottom (polling adds new msgs), auto-scroll if near bottom
  useEffect(() => {
    if (!initialScrollDone.current) return;
    const prevCount = prevMessageCountRef.current;
    const newCount = messages.length;

    if (newCount > prevCount && prevCount > 0) {
      if (isNearBottomRef.current) {
        requestAnimationFrame(() => scrollToBottom("smooth"));
      }
    }
    prevMessageCountRef.current = newCount;
  }, [messages.length, scrollToBottom]);

  // When older messages are prepended, preserve scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !initialScrollDone.current) return;

    const prevHeight = prevScrollHeightRef.current;
    const newHeight = el.scrollHeight;

    if (newHeight > prevHeight && prevHeight > 0 && !isNearBottomRef.current) {
      el.scrollTop += newHeight - prevHeight;
    }
    prevScrollHeightRef.current = newHeight;
  });

  // Track whether user is near the bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 100;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    prevScrollHeightRef.current = el.scrollHeight;
  }, []);

  // IntersectionObserver for top sentinel — triggers loading older messages
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = scrollRef.current;
    if (!sentinel || !container || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasMore && !loadingMore) {
          onLoadMore();
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSend(text.trim());
      setText("");
      requestAnimationFrame(() => scrollToBottom("smooth"));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" }) +
      " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  let lastDateLabel = "";

  return (
    <div className="flex flex-col h-full">
      {/* Messages area with subtle gradient */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0"
        style={{ background: "linear-gradient(180deg, rgba(168,85,247,0.04) 0%, rgba(236,72,153,0.03) 30%, transparent 60%)" }}
      >
        {/* Top sentinel for infinite scroll */}
        <div ref={topSentinelRef} className="h-1" />

        {/* Loading older messages indicator */}
        {loadingMore && (
          <div className="flex justify-center py-3">
            <div className="flex items-center gap-2 text-app-text-muted text-info bg-app-surface-raised px-4 py-2 rounded-full shadow-sm border border-app-border/30">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading older messages…</span>
            </div>
          </div>
        )}

        {/* "No more messages" indicator */}
        {!hasMore && messages.length > 0 && (
          <div className="flex justify-center py-2">
            <span className="text-[11px] text-app-text-muted bg-app-surface-raised px-3 py-0.5 rounded-full">
              Beginning of conversation
            </span>
          </div>
        )}

        {messages.length === 0 && !loadingMore && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="text-5xl" role="img" aria-label="chat">💌</span>
            <p className="text-center text-app-text-muted text-body font-medium">
              No messages yet!
            </p>
            <p className="text-center text-app-text-muted text-info">
              Send a message to start the conversation ✨
            </p>
          </div>
        )}
        {messages.map((msg) => {
          const dateLabel = getDateLabel(msg.date);
          const showDateSep = dateLabel !== lastDateLabel;
          lastDateLabel = dateLabel;

          return (
            <div key={msg._key}>
              {/* Date separator */}
              {showDateSep && (
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-app-border" />
                  <span className="text-[11px] font-semibold text-app-text-muted bg-app-surface-raised px-3 py-0.5 rounded-full shadow-sm">
                    {dateLabel}
                  </span>
                  <div className="flex-1 h-px bg-app-border" />
                </div>
              )}

              {msg.is_system ? (
                <div className="text-center my-2 animate-[fadeInUp_0.3s_ease-out]">
                  {(() => {
                    const style = getSystemMessageStyle(msg);
                    return (
                      <span className={`inline-flex items-center gap-1.5 text-info ${style.text} ${style.bg} border px-3 py-1.5 rounded-full shadow-sm`}>
                        <span className={style.accent}>{style.icon}</span>
                        {formatSystemMessage(msg)}
                      </span>
                    );
                  })()}
                </div>
              ) : (
                <div
                  className={`flex ${msg.from === currentUserId ? "justify-end" : "justify-start"} animate-[fadeInUp_0.25s_ease-out]`}
                >
                  {msg.message_type === "image" && decryptBinary ? (
                    <div className={`flex flex-col gap-0.5 ${msg.from === currentUserId ? "items-end" : "items-start"}`}>
                      <div
                        className={`rounded-2xl overflow-hidden shadow-sm ${
                          msg.from === currentUserId ? "rounded-br-sm" : "rounded-bl-sm"
                        }`}
                      >
                        <ChatImageBubble
                          encryptedData={msg.encrypted_message}
                          decryptBinary={decryptBinary}
                          onClick={setLightboxSrc}
                        />
                      </div>
                      <p className={`text-[10px] px-1 ${msg.from === currentUserId ? "text-app-text-muted" : "text-app-text-muted"}`}>
                        {formatTime(msg.date)}
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`max-w-[80%] px-3.5 py-2 rounded-2xl shadow-sm transition-all ${
                        msg.from === currentUserId
                          ? "bg-gradient-to-br from-app-primary to-purple-600 text-white rounded-br-sm"
                          : "bg-app-surface-raised text-app-text rounded-bl-sm border border-app-border/30"
                      }`}
                    >
                      <p className="text-body break-words">{msg.encrypted_message}</p>
                      <p
                        className={`text-[10px] mt-0.5 ${
                          msg.from === currentUserId ? "text-white/60" : "text-app-text-muted"
                        }`}
                      >
                        {formatTime(msg.date)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Typing indicator */}
        {sending && <TypingIndicator />}

        {/* Bottom anchor for auto-scroll */}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-app-border bg-app-surface/80 backdrop-blur-sm p-2.5 flex gap-2 items-center">
        {onCaptureImage && (
          <Button
            size="icon"
            type="button"
            variant="ghost"
            onClick={onCaptureImage}
            disabled={sending}
            className="shrink-0 rounded-full h-9 w-9 text-app-text-muted hover:text-app-primary hover:bg-app-primary/10 transition-all"
            title="Send a photo"
          >
            <Camera className="w-5 h-5" />
          </Button>
        )}
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message... 💬"
          className="flex-1 rounded-full px-4 border-app-border/50 focus:border-app-primary/50 transition-colors"
          disabled={sending}
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={!text.trim() || sending}
          className="bg-gradient-to-r from-app-primary to-purple-600 hover:from-app-primary-hover hover:to-purple-700 shrink-0 rounded-full shadow-md hover:shadow-lg transition-all h-9 w-9"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}

      {/* Keyframe animation for message fade-in */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
